import { useEffect } from "react";
import { useLocale } from "@/i18n/LocaleContext";

interface SEOHeadProps {
  title: string;
  description: string;
  canonical: string;
  ogType?: string;
  ogImage?: string;
  keywords?: string;
  ogDescription?: string;
  twitterDescription?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const BASE_URL = "https://www.regularizacionmasivaespana.es";

// Maps of ES path → EN / FR equivalents for hreflang generation
const ROUTE_MAP_EN: Record<string, string> = {
  "/": "/en",
  "/sobre-nosotros": "/en/about-us",
  "/espacio-informativo": "/en/resources",
  "/aviso-legal": "/en/legal-notice",
  "/politica-privacidad": "/en/privacy-policy",
  "/politica-cookies": "/en/cookie-policy",
  "/cita-confirmada": "/en/booking-confirmed",
};
const ROUTE_MAP_FR: Record<string, string> = {
  "/": "/fr",
  "/sobre-nosotros": "/fr/a-propos",
  "/espacio-informativo": "/fr/ressources",
  "/aviso-legal": "/fr/mentions-legales",
  "/politica-privacidad": "/fr/politique-confidentialite",
  "/politica-cookies": "/fr/politique-cookies",
  "/cita-confirmada": "/fr/rendez-vous-confirme",
};
const REVERSE_EN = Object.fromEntries(Object.entries(ROUTE_MAP_EN).map(([es, en]) => [en, es]));
const REVERSE_FR = Object.fromEntries(Object.entries(ROUTE_MAP_FR).map(([es, fr]) => [fr, es]));

const getHreflangSet = (canonical: string): { es: string; en: string; fr: string } | null => {
  const path = canonical.startsWith("http") ? new URL(canonical).pathname : canonical;

  let esPath: string | null = null;
  if (ROUTE_MAP_EN[path]) esPath = path;
  else if (REVERSE_EN[path]) esPath = REVERSE_EN[path];
  else if (REVERSE_FR[path]) esPath = REVERSE_FR[path];

  if (!esPath) {
    const esBlog = path.match(/^\/espacio-informativo\/(.+)$/);
    const enBlog = path.match(/^\/en\/resources\/(.+)$/);
    const frBlog = path.match(/^\/fr\/ressources\/(.+)$/);
    if (esBlog) {
      return {
        es: path,
        en: `/en/resources/${esBlog[1]}`,
        fr: `/fr/ressources/${esBlog[1]}`,
      };
    }
    if (enBlog) {
      return {
        es: `/espacio-informativo/${enBlog[1]}`,
        en: path,
        fr: `/fr/ressources/${enBlog[1]}`,
      };
    }
    if (frBlog) {
      return {
        es: `/espacio-informativo/${frBlog[1]}`,
        en: `/en/resources/${frBlog[1]}`,
        fr: path,
      };
    }
    return null;
  }

  return {
    es: esPath,
    en: ROUTE_MAP_EN[esPath] ?? "/en",
    fr: ROUTE_MAP_FR[esPath] ?? "/fr",
  };
};

const setMeta = (attr: string, key: string, content: string) => {
  let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel: string, href: string, attrs?: Record<string, string>) => {
  const selector = attrs
    ? `link[rel="${rel}"]${Object.entries(attrs).map(([k, v]) => `[${k}="${v}"]`).join("")}`
    : `link[rel="${rel}"]`;
  let el = document.querySelector(selector) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (attrs) Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const removeLinks = (selector: string) => {
  document.querySelectorAll(selector).forEach((el) => el.remove());
};

const SEOHead = ({
  title,
  description,
  canonical,
  ogType = "website",
  ogImage = `${BASE_URL}/og-image.jpg`,
  keywords,
  ogDescription,
  twitterDescription,
  jsonLd,
}: SEOHeadProps) => {
  const { locale } = useLocale();

  useEffect(() => {
    const fullTitle = `${title} | Equalitas Abogados`;
    const fullCanonical = canonical.startsWith("http") ? canonical : `${BASE_URL}${canonical}`;

    document.title = fullTitle;
    document.documentElement.lang = locale;

    setMeta("name", "description", description);
    if (keywords) setMeta("name", "keywords", keywords);

    setLink("canonical", fullCanonical);

    // Open Graph
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", ogDescription || description);
    setMeta("property", "og:url", fullCanonical);
    setMeta("property", "og:type", ogType);
    setMeta("property", "og:image", ogImage);
    setMeta(
      "property",
      "og:locale",
      locale === "en" ? "en_US" : locale === "fr" ? "fr_FR" : "es_ES"
    );
    setMeta("property", "og:site_name", "Equalitas Abogados");

    // Twitter
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", twitterDescription || description);
    setMeta("name", "twitter:image", ogImage);

    // Hreflang
    removeLinks('link[rel="alternate"][hreflang]');
    const set = getHreflangSet(canonical);
    if (set) {
      setLink("alternate", `${BASE_URL}${set.es}`, { hreflang: "es" });
      setLink("alternate", `${BASE_URL}${set.en}`, { hreflang: "en" });
      setLink("alternate", `${BASE_URL}${set.fr}`, { hreflang: "fr" });
      setLink("alternate", `${BASE_URL}${set.es}`, { hreflang: "x-default" });
    }

    // JSON-LD
    let scriptEl = document.getElementById("seo-jsonld") as HTMLScriptElement | null;
    if (jsonLd) {
      if (!scriptEl) {
        scriptEl = document.createElement("script");
        scriptEl.id = "seo-jsonld";
        scriptEl.type = "application/ld+json";
        document.head.appendChild(scriptEl);
      }
      const ldContent = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      scriptEl.textContent = JSON.stringify(ldContent.length === 1 ? ldContent[0] : ldContent);
    } else if (scriptEl) {
      scriptEl.remove();
    }

    return () => {
      document.getElementById("seo-jsonld")?.remove();
      removeLinks('link[rel="alternate"][hreflang]');
    };
  }, [title, description, canonical, ogType, ogImage, keywords, jsonLd, locale]);

  return null;
};

export { BASE_URL };
export default SEOHead;
